import { View } from "ui/atoms/view";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { CreateMedicationFormModel } from "../types/CreateMedicationFormModel";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { MedicationFormDropdownOptions } from "app/services/api/types/medications/enums/MedicationFormEnum";
import { MedicationAdministrationDropdownOptions } from "app/services/api/types/medications/enums/MedicationAdministrationMethodEnum";
import { FrequencyDropdownOptions, FrequencyEnum } from "app/services/api/types/medications/enums/FrequencyEnum";
import { MedicationTimeOfDayDropdownOptions } from "app/services/api/types/medications/enums/MedicationTimeOfDayEnum";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon } from "ui/gluestack/icon";
import { useHandleMedication } from "../hooks/useHandleMedication";
import { MedicationModel } from "app/services/api/types/medications/models/MedicationModel";
import { useRouter } from "expo-router";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { useWatch } from "react-hook-form";
import { FormControlledMultiSelectDropdown } from "ui/molecules/form/FormControlledMultiSelectDropdown";

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];
interface Props {
  initialMedication?: MedicationModel;
  onClose: () => void;
  isEditing?: boolean;
}
export default function CreateMedicationFormTemplate({
  initialMedication,
  onClose,
  isEditing,
}: Props) {
  const router = useRouter();
  const { control, submit, isLoadingAction } = useHandleMedication({
    initialMedication,
    onUpdateSuccess: (newMedicationId) => {
      router.replace(
        `/residents/${initialMedication?.resident_id}/medications/${newMedicationId}`,
      );
      onClose();
    },
    onCreateSuccess: () => {
      onClose();
    },
  });

  const frequency = useWatch({
    control,
    name: "frequency",
  });

  const isWeeklyBased = [FrequencyEnum.OW, FrequencyEnum.BW].includes(
    frequency,
  );
  const isMonthlyBased = [FrequencyEnum.OM, FrequencyEnum.BM].includes(
    frequency,
  );

  return (
    <View className="grid md:grid-cols-3 gap-8">
      <FormControlledInput<CreateMedicationFormModel>
        id="med_name"
        name="name"
        control={control}
        label="Medication name"
        placeholder="Enter name of medication"
        isRequired
        isDisabled={isLoadingAction}
      />
      <FormControlledInput<CreateMedicationFormModel>
        id="med_diagnosis"
        name="diagnosis"
        control={control}
        label="Diagnosis"
        placeholder="Enter diagnosis"
        isDisabled={isLoadingAction}
      />
      <FormControlledInput<CreateMedicationFormModel>
        id="med_dosage"
        name="dosage"
        control={control}
        label="Dosage and unit of measure"
        placeholder="Enter dosage and unit of measure"
        isRequired
        isDisabled={isLoadingAction}
      />
      <FormControlledDropdown<CreateMedicationFormModel>
        name="form"
        control={control}
        label="Form of medication"
        placeholder={"Select medication form"}
        isRequired
        isDisabled={isLoadingAction}
        options={MedicationFormDropdownOptions}
      />
      <FormControlledDropdown<CreateMedicationFormModel>
        name="administration_method"
        control={control}
        label="Method of administration"
        placeholder={"Select administration method"}
        isRequired
        isDisabled={isLoadingAction}
        options={MedicationAdministrationDropdownOptions}
      />
      <FormControlledDropdown<CreateMedicationFormModel>
        name="frequency"
        control={control}
        label="Frequency of medication"
        placeholder={"Select frequency of medication"}
        isRequired
        isDisabled={isLoadingAction}
        options={FrequencyDropdownOptions}
      />
      <FormControlledInput<CreateMedicationFormModel>
        id="prescribed_by"
        name="prescribed_by"
        control={control}
        label="Prescribed By"
        placeholder="Enter name"
        isDisabled={isLoadingAction}
        isRequired
      />
      <FormControlledDatePicker
        id="start_date"
        name="start_date"
        control={control}
        label="Start date"
        placeholder={"00/00/0000"}
        isRequired
      />
      <FormControlledDatePicker
        id="end_date"
        name="end_date"
        control={control}
        label="End date"
        placeholder={"00/00/0000"}
      />
      {isWeeklyBased && (
        <FormControlledMultiSelectDropdown<CreateMedicationFormModel>
          name="day_of_week"
          control={control}
          label="Days of week"
          placeholder="Select day(s)"
          isRequired
          isDisabled={isLoadingAction}
          options={DAYS_OF_WEEK}
          rules={{
            validate: {
              size: (value) => {
                if (!value || value.length === 0) return "At least one day is required";
                return true;
              },
            },
          }}
        />
      )}
      {isMonthlyBased && (
        <FormControlledInput<CreateMedicationFormModel>
          id="day_of_month"
          name="day_of_month"
          control={control}
          label="Day of month"
          placeholder="Enter day (1-31)"
          type="numeric"
          isRequired
          isDisabled={isLoadingAction}
          rules={{
            validate: {
              range: (value) => {
                if (value === undefined || value === null || value === "") {
                  return "Day of month is required";
                }
                const num = Number(value);
                if (isNaN(num)) return "Must be a number";
                if (num < 1 || num > 31) return "Day must be between 1 and 31";
                return true;
              },
            },
          }}
        />
      )}
      {(isWeeklyBased || isMonthlyBased) && (
        <FormControlledDropdown<CreateMedicationFormModel>
          name="time_of_day"
          control={control}
          label="Time of day"
          placeholder={"Select time of day"}
          isRequired
          isDisabled={isLoadingAction}
          options={MedicationTimeOfDayDropdownOptions}
        />
      )}
      <View className="col-span-full">
        <FormControlledInput<CreateMedicationFormModel>
          id="med_notes"
          name="notes"
          control={control}
          label="Notes"
          placeholder="Enter note"
          isDisabled={isLoadingAction}
        />
      </View>
      <View className="col-span-full">
        <View className="flex flex-row justify-end gap-x-4 mt-10 pb-4">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            size="sm"
            onPress={submit}
            disabled={isLoadingAction}
          >
            {isEditing ? (
              <ButtonText className="text-white">Save changes</ButtonText>
            ) : (
              <>
                <ButtonText className="text-white">
                  Create medication
                </ButtonText>
                <ButtonIcon as={AddIcon} className="w-4 h-4 stroke-white" />
              </>
            )}
          </Button>
        </View>
      </View>
    </View>
  );
}
